<template>
  <div class="m-page">

    <!-- 1면: 메인 이미지 -->
    <img class="m-img" src="@/assets/mobile/1_9x16.png" alt="" />
    <img class="m-img" src="@/assets/mobile/1x1.png" alt="" />

    <!-- 2면: 사업개요 -->
    <section class="m-overview">
      <div class="m-inner">
        <span class="m-eyebrow">BUSINESS SUMMARY</span>
        <h2 class="m-title">사업개요</h2>

        <!-- 조감도 -->
        <img class="m-birdseye" src="@/assets/hero/hero2.jpg" alt="테크노 레이원시티 조감도" />

        <ul class="m-info">
          <li v-for="(row, i) in overview" :key="i">
            <span class="m-info-label">{{ row.label }}</span>
            <span class="m-info-value">{{ row.value }}</span>
          </li>
        </ul>

        <p class="m-note">
          ※ 상기 면적은 계획면적으로 세부계획 시 변경될 수 있습니다.
        </p>
      </div>
    </section>

    <!-- 3면: 입지환경 -->
    <section class="m-location">
      <div class="m-inner">
        <span class="m-eyebrow">PREMIUM LOCATION</span>
        <h2 class="m-title">입지환경</h2>
        <p class="m-lead">
          청주가 주목하는 <strong>특급입지</strong><br />
          모두가 기대하는 <strong>생활가치</strong>
        </p>

        <!-- 입지 이미지 -->
        <img class="m-loc-img" :src="locImg1" alt="입지환경 1" />
        <img class="m-loc-img" :src="locImg2" alt="입지환경 2" />

        <!-- 광역교통 인프라 -->
        <div class="m-traffic">
          <span class="m-sub-eyebrow">TRAFFIC</span>
          <h3 class="m-sub-title">광역교통 인프라</h3>

          <div class="m-traffic-list">
            <div class="m-traffic-card" v-for="(item, i) in trafficItems" :key="i">
              <span class="m-traffic-no">{{ String(i + 1).padStart(2, '0') }}</span>
              <h4 class="m-traffic-t">{{ item.title }}</h4>
              <span class="m-traffic-line"></span>
              <p class="m-traffic-d">{{ item.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 4면: 단지정보 -->
    <section class="m-product">
      <div class="m-inner">
        <span class="m-eyebrow">LUXURY RESIDENCE</span>
        <h2 class="m-title">단지정보</h2>
        <p class="m-lead">
          당신의 일상에 <strong>럭셔리 라이프</strong>를<br />
          선물합니다
        </p>
        <p class="m-sublead">청주에 없었던 꿈 같은 럭셔리 주거문화</p>

        <!-- 단지정보 이미지 -->
        <img class="m-prod-img" :src="prodImg1" alt="단지정보 1" />
        <img class="m-prod-img" :src="prodImg2" alt="단지정보 2" />
      </div>
    </section>

    <!-- 5면: 프리미엄 -->
    <section class="m-premium">
      <div class="m-inner">
        <span class="m-eyebrow">PREMIUM VALUE</span>
        <h2 class="m-title">프리미엄</h2>
        <p class="m-lead">
          청주에 없었던<br /><strong>꿈같은 생활특권</strong>
        </p>
        <span class="m-badge">역대급 혜택</span>
        <p class="m-sec-desc">
          청주의 경제와 비전을 이끌어가는 청주테크노폴리스와 연계한 생활특권,
          프리미엄을 넘어선 럭셔리 아파트가 청주에 떠오르다
        </p>

        <div class="m-prem-list">
          <div class="m-prem-card" v-for="(item, i) in premiumItems" :key="i">
            <span class="m-prem-no">{{ String(i + 1).padStart(2, '0') }}</span>
            <div class="m-prem-body">
              <p class="m-prem-top">{{ item.top }}</p>
              <h4 class="m-prem-t">{{ item.title }}</h4>
            </div>
          </div>
        </div>

        <!-- 단지설계 -->
        <div class="m-design2">
          <p class="m-design2-small">일상이 힐링이 되는</p>
          <h3 class="m-design2-title">차원 높은 단지설계</h3>
          <p class="m-sec-desc">대단지 안에 명품공원이 부럽지 않은 단지 내 조경과 혁신적인 평면설계</p>

          <div class="m-design2-grid">
            <div class="m-design2-card" v-for="(d, i) in designItems" :key="i">
              <span class="m-design2-no">{{ String(i + 1).padStart(2, '0') }}</span>
              <h4 class="m-design2-t">{{ d.title }}</h4>
              <p class="m-design2-d">{{ d.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 6면: 세대안내 -->
    <section class="m-type">
      <div class="m-inner">
        <span class="m-eyebrow">UNIT TYPE</span>
        <h2 class="m-title">세대안내</h2>
        <p class="m-sublead">현대 테크노 레이원시티 평면 안내</p>

        <div class="m-type-tabs">
          <button
            v-for="t in types"
            :key="t.id"
            class="m-type-tab"
            :class="{ 'is-active': activeSize === t.id }"
            @click="activeSize = t.id"
          >{{ t.label }}</button>
        </div>

        <div class="m-type-img">
          <transition name="m-fade" mode="out-in">
            <img :key="activeSize" :src="currentType.img" :alt="currentType.label + ' 평면도'" />
          </transition>
        </div>
      </div>
    </section>

    <!-- 7면: 커뮤니티 -->
    <section class="m-comm">
      <div class="m-inner">
        <span class="m-eyebrow">COMMUNITY</span>
        <h2 class="m-title">커뮤니티</h2>
        <p class="m-lead">
          일상을 힐링으로 채워줄<br /><strong>커뮤니티의 신세계</strong>
        </p>

        <img class="m-prod-img" :src="commBg" alt="커뮤니티" />

        <div class="m-comm-list">
          <div class="m-comm-card" v-for="(c, i) in communityItems" :key="i">
            <span class="m-comm-no">{{ String(i + 1).padStart(2, '0') }}</span>
            <h4 class="m-comm-t">{{ c.title }}</h4>
            <span class="m-comm-line"></span>
            <p class="m-comm-d">{{ c.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- 8면: 오시는 길 -->
    <section class="m-contact">
      <div class="m-inner">
        <span class="m-eyebrow">FIND US</span>
        <h2 class="m-title">오시는 길</h2>

        <div class="m-map-card" v-for="(m, i) in contacts" :key="i">
          <div class="m-map-img">
            <img :src="m.img" :alt="m.label + ' 위치'" />
            <span v-if="m.open" class="m-grand">GRAND<br />OPEN</span>
          </div>
          <div class="m-map-info">
            <span class="m-map-label">{{ m.label }}</span>
            <span class="m-map-addr">{{ m.address }}</span>
            <div class="m-map-btns">
              <a :href="m.naver" target="_blank" rel="noopener" class="m-map-btn">
                <b class="n">N</b> 네이버 지도
              </a>
              <a :href="m.kakao" target="_blank" rel="noopener" class="m-map-btn">
                <b class="k">K</b> 카카오 지도
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 9면: 방문예약 -->
    <section class="m-reserve">
      <div class="m-inner">
        <span class="m-eyebrow gold">RESERVATION</span>
        <h2 class="m-title light">방문예약</h2>
        <p class="m-reserve-desc">
          청주 테크노 레이원시티에 관심을 가져주셔서 감사합니다.<br />
          아래 버튼으로 신청해주시면 상담원이 빠르게 연락드리겠습니다.
        </p>

        <a href="tel:1877-4088" class="m-reserve-phone">
          <span class="m-reserve-phone-label">대표문의</span>
          <span class="m-reserve-phone-num">1877-4088</span>
        </a>

        <button class="m-reserve-btn" @click="openRegister">관심고객 등록하기</button>
      </div>
    </section>

    <!-- 푸터 -->
    <footer class="m-footer">
      <div class="m-footer-brand">청주 테크노 레이원시티</div>

      <div class="m-footer-info">
        <div class="m-footer-row">
          <span>현장위치</span>
          <b>청주시 흥덕구 송절동 산 76-2번지 일원</b>
        </div>
        <div class="m-footer-row">
          <span>홈페이지 운영</span>
          <b>주식회사 더한걸음</b>
        </div>
        <div class="m-footer-row">
          <span>이메일</span>
          <b><a href="mailto:info@step1.kr">info@step1.kr</a></b>
        </div>
      </div>

      <div class="m-footer-notes">
        <p>본 사이트는 청주 테크노 레이원시티 분양 상담 및 방문 예약 안내를 위한 홍보 상담 페이지입니다.</p>
        <p>시행사 및 시공사의 공식 홈페이지가 아니며, 분양 관련 상세 정보는 모델하우스 상담을 통해 안내받으실 수 있습니다.</p>
      </div>

      <div class="m-footer-copy">
        <span>Copyright ⓒ 2026 주식회사 더한걸음.</span>
        <router-link to="/privacy">개인정보처리방침</router-link>
      </div>
    </footer>

  </div>
</template>

<script>
import locImg1 from '@/assets/location/입지1.avif'
import locImg2 from '@/assets/location/입지2.avif'
import prodImg1 from '@/assets/product/상품1.avif'
import prodImg2 from '@/assets/product/상품2.avif'
import commBg from '@/assets/product/커뮤니티.avif'
import type59 from '@/assets/unit/59.avif'
import type74 from '@/assets/unit/74.webp'
import type84A from '@/assets/unit/84A.webp'
import type84A1 from '@/assets/unit/84A-1.webp'
import type84B from '@/assets/unit/84B.webp'
import contact1 from '@/assets/location1.avif'
import contact2 from '@/assets/location2.avif'

export default {
  name: 'MobileView',
  data() {
    return {
      locImg1,
      locImg2,
      prodImg1,
      prodImg2,
      commBg,
      activeSize: '59',
      overview: [
        { label: '대지위치', value: '충청북도 청주시 서원구 장성동 204번지 일원' },
        { label: '대지면적', value: '62,267.00㎡' },
        { label: '건축면적', value: '7,381.22㎡' },
        { label: '규모', value: '지하 2층 ~ 지상 35층 / 18개동 / 총 1,817세대' },
        { label: '건폐율 / 용적률', value: '16.25% / 226.26%' },
        { label: '주차대수', value: '2,045대 (세대당 1.41대)' }
      ],
      trafficItems: [
        {
          title: '고속철도(KTX) 오송역',
          desc: '서울역(50분대), 부산역(1시간 40분대)으로 바로 이동할 수 있어 전국 2시간대 생활권'
        },
        {
          title: '복선전철 북청주역(예정)',
          desc: '천안~청주공항 복선전철 북청주역 개통 예정(2029년)으로 청주 테크노폴리스로의 접근이 용이'
        },
        {
          title: '다양한 쾌속루트',
          desc: '청주2순환로, 중부고속도로 서청주IC가 인접해 빠르게 이용할 수 있는 다양한 광역교통망'
        },
        {
          title: '청주국제공항 항공루트',
          desc: '제주행 국내선과 일부 국제선까지 운행하고 있어 가까이에서 편리하게 항공편 이용 가능'
        }
      ],
      premiumItems: [
        { top: '청주테크노폴리스', title: '직주근접 기업도시의 비전' },
        { top: '쉐프가 직접 만드는', title: '입주민 조식 & 중식 서비스 제공' },
        { top: '단지 내 YBM 영어마을', title: '2년 무상교육 제공' },
        { top: '물놀이시설 · 다목적체육시설 · 캠핑장 등', title: '스포츠 · 레저 커뮤니티' },
        { top: '단지 앞 황톳길과 무심천공원', title: '프리미엄 대단지 조성' },
        { top: '취득세 · 종부세 · 건보료 등', title: '다양한 세제혜택, 주택수 미포함' }
      ],
      designItems: [
        { title: '대단지 스케일', desc: '인근에서 가장 규모가 큰 지역 내 랜드마크 단지' },
        { title: '단지 내 광장', desc: '중앙광장 · 공유마당 · 문화마당 등 입주민 광장 조성' },
        { title: '남향 위주 배치', desc: '최적의 일조량을 확보하는 남향 위주 설계' },
        { title: '주민운동시설', desc: '입주민 건강관리를 위해 가볍게 운동할 수 있는 체육시설' },
        { title: '어린이 놀이터', desc: '상상력과 창의력을 키워주며 마음껏 뛰어놀 수 있는 놀이터' },
        { title: '다목적 공간활용', desc: '팬트리 · 가변형 알파룸 적용으로 높은 공간 활용성 연출' }
      ],
      types: [
        { id: '59', label: '59㎡', img: type59 },
        { id: '74', label: '74㎡', img: type74 },
        { id: '84A', label: '84㎡A', img: type84A },
        { id: '84A-1', label: '84㎡A-1', img: type84A1 },
        { id: '84B', label: '84㎡B', img: type84B }
      ],
      communityItems: [
        { title: '단지 내 YBM 영어마을', desc: '원어민 전문강사의 활동중심 체계적인 영어수업 진행' },
        { title: '입주민 조식 & 중식서비스', desc: 'CJ프레시웨이 쉐프가 만드는 조·중식을 공용식당에서 제공' },
        { title: '단지 내 골프장', desc: '유명 골프장을 시뮬레이션한 골프존 최신 스크린 골프' },
        { title: '물놀이시설', desc: '안전한 수심과 그늘 조경을 갖춘 여름철 힐링공간' },
        { title: '패밀리 캠핑장', desc: '개별 데크와 바비큐 시설이 어우러진 단지 내 캠핑장' },
        { title: '영화감상실', desc: '대형 스크린과 안락좌석의 프라이빗 시네마' },
        { title: '황톳길 산책로', desc: '나무 그늘 사이로 이어지는 사색의 힐링로드' },
        { title: '입주민 전용카페', desc: '단지 정원을 조망하며 즐기는 이웃과의 소통공간' }
      ],
      contacts: [
        {
          label: '하우징갤러리',
          address: '충북 청주시 청원구 주중동 161-1',
          img: contact1,
          open: true,
          naver: 'https://map.naver.com/v5/search/%EC%B2%AD%EC%A3%BC%EC%8B%9C%20%EC%B2%AD%EC%9B%90%EA%B5%AC%20%EC%A3%BC%EC%A4%91%EB%8F%99%20161-1',
          kakao: 'https://map.kakao.com/?q=%EC%B2%AD%EC%A3%BC%EC%8B%9C%20%EC%B2%AD%EC%9B%90%EA%B5%AC%20%EC%A3%BC%EC%A4%91%EB%8F%99%20161-1'
        },
        {
          label: '현 장',
          address: '충북 청주시 흥덕구 송절동 산 76-2번지 일원',
          img: contact2,
          open: false,
          naver: 'https://map.naver.com/v5/search/%EC%B2%AD%EC%A3%BC%EC%8B%9C%20%ED%9D%A5%EB%8D%95%EA%B5%AC%20%EC%86%A1%EC%A0%88%EB%8F%99%20%EC%82%B0%2076-2',
          kakao: 'https://map.kakao.com/?q=%EC%B2%AD%EC%A3%BC%EC%8B%9C%20%ED%9D%A5%EB%8D%95%EA%B5%AC%20%EC%86%A1%EC%A0%88%EB%8F%99%20%EC%82%B0%2076-2'
        }
      ]
    }
  },
  computed: {
    currentType() {
      return this.types.find((t) => t.id === this.activeSize) || this.types[0]
    }
  },
  methods: {
    openRegister() {
      window.dispatchEvent(new CustomEvent('open-register-modal'))
    }
  }
}
</script>

<style scoped>
.m-page {
  width: 100%;
  background: #fff;
  /* 고정 헤더(탑바 56px) 높이만큼 내림 */
  padding-top: 56px;
}

/* 이미지 - 풀블리드 */
.m-img {
  display: block;
  width: 100%;
  height: auto;
  vertical-align: top;
  margin: 0;
}

/* ===== 사업개요 ===== */
.m-overview {
  background: #fff;
}

.m-inner {
  padding: 56px 24px;
}

/* 조감도 - 제목 아래 */
.m-birdseye {
  display: block;
  width: 100%;
  height: auto;
  border-radius: 8px;
  margin: 4px 0 28px;
}

.m-eyebrow {
  display: block;
  font-family: var(--font-pretendard);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 3px;
  color: var(--rayone-primary);
  margin-bottom: 8px;
}

.m-title {
  font-family: var(--font-pretendard);
  font-size: 30px;
  font-weight: 700;
  color: #1a1a1a;
  letter-spacing: -0.5px;
  margin: 0 0 28px;
}

.m-title.light {
  color: #fff;
}

.m-info {
  list-style: none;
  padding: 0;
  margin: 0 0 20px;
}

.m-info li {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 16px 0;
  border-top: 1px solid #eee;
}

.m-info li:last-child {
  border-bottom: 1px solid #eee;
}

.m-info-label {
  font-family: var(--font-pretendard);
  font-size: 13px;
  font-weight: 700;
  color: var(--rayone-primary);
  letter-spacing: 0.5px;
}

.m-info-value {
  font-family: var(--font-pretendard);
  font-size: 15px;
  font-weight: 400;
  color: #333;
  line-height: 1.5;
  word-break: keep-all;
}

.m-note {
  font-family: var(--font-pretendard);
  font-size: 11px;
  color: #999;
  line-height: 1.6;
  margin: 0;
  word-break: keep-all;
}

/* ===== 입지환경 ===== */
.m-location {
  background: #fff;
  border-top: 1px solid #f0f0f0;
}

.m-lead {
  font-family: var(--font-pretendard);
  font-size: 20px;
  font-weight: 300;
  color: #444;
  line-height: 1.6;
  letter-spacing: -0.3px;
  margin: 0 0 28px;
  word-break: keep-all;
}

.m-lead strong {
  font-weight: 700;
  color: #1a1a1a;
}

.m-loc-img {
  display: block;
  width: 100%;
  height: auto;
  border-radius: 8px;
  margin-bottom: 16px;
}

/* 광역교통 */
.m-traffic {
  margin-top: 28px;
}

.m-sub-eyebrow {
  display: block;
  font-family: var(--font-pretendard);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 4px;
  color: var(--rayone-primary);
  margin-bottom: 8px;
}

.m-sub-title {
  font-family: var(--font-pretendard);
  font-size: 22px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 20px;
}

.m-traffic-list {
  border-top: 2px solid #1a1a1a;
}

.m-traffic-card {
  padding: 22px 0;
  border-bottom: 1px solid #eee;
}

.m-traffic-no {
  font-family: var(--font-paperlogy);
  font-size: 24px;
  font-weight: 700;
  color: var(--rayone-primary);
  opacity: 0.35;
}

.m-traffic-t {
  font-family: var(--font-pretendard);
  font-size: 16px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 10px 0 0;
  line-height: 1.4;
  word-break: keep-all;
}

.m-traffic-line {
  display: block;
  width: 24px;
  height: 1px;
  background: var(--rayone-primary);
  margin: 12px 0;
}

.m-traffic-d {
  font-family: var(--font-pretendard);
  font-size: 13px;
  font-weight: 400;
  color: #777;
  line-height: 1.7;
  margin: 0;
  word-break: keep-all;
}

/* ===== 단지정보 ===== */
.m-product {
  background: #fff;
  border-top: 1px solid #f0f0f0;
}

.m-sublead {
  font-family: var(--font-pretendard);
  font-size: 13px;
  font-weight: 500;
  color: #999;
  letter-spacing: 2px;
  margin: 0 0 28px;
  word-break: keep-all;
}

.m-prod-img {
  display: block;
  width: 100%;
  height: auto;
  border-radius: 8px;
  margin-bottom: 16px;
}

/* 공통 서브 설명 */
.m-sec-desc {
  font-family: var(--font-pretendard);
  font-size: 13px;
  font-weight: 400;
  color: #777;
  line-height: 1.8;
  margin: 0 0 24px;
  word-break: keep-all;
}

/* ===== 프리미엄 ===== */
.m-premium {
  background: #faf9f7;
  border-top: 1px solid #f0f0f0;
}

.m-badge {
  display: inline-block;
  padding: 7px 22px;
  border: 1px solid #c9a96e;
  color: #c9a96e;
  font-family: var(--font-pretendard);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 2px;
  margin: 0 0 20px;
}

.m-prem-list {
  border-top: 1px solid #e0e0e0;
  margin-bottom: 44px;
}

.m-prem-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 0;
  border-bottom: 1px solid #e0e0e0;
}

.m-prem-no {
  font-family: var(--font-paperlogy);
  font-size: 32px;
  font-weight: 700;
  color: #c9a96e;
  opacity: 0.4;
  line-height: 1;
  flex-shrink: 0;
  width: 44px;
}

.m-prem-top {
  font-family: var(--font-pretendard);
  font-size: 12px;
  font-weight: 400;
  color: #999;
  margin: 0 0 4px;
  line-height: 1.4;
  word-break: keep-all;
}

.m-prem-t {
  font-family: var(--font-pretendard);
  font-size: 16px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
  line-height: 1.4;
  word-break: keep-all;
}

/* 단지설계 */
.m-design2 {
  padding-top: 36px;
  border-top: 1px solid #e5e5e5;
  text-align: center;
}

.m-design2-small {
  font-family: var(--font-pretendard);
  font-size: 13px;
  color: #999;
  letter-spacing: 2px;
  margin: 0 0 8px;
}

.m-design2-title {
  font-family: var(--font-pretendard);
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 14px;
}

.m-design2 .m-sec-desc {
  text-align: center;
}

.m-design2-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  border-top: 2px solid #1a1a1a;
  text-align: left;
}

.m-design2-card {
  padding: 20px 14px;
  border-bottom: 1px solid #eee;
}

.m-design2-card:nth-child(odd) {
  border-right: 1px solid #eee;
}

.m-design2-no {
  font-family: var(--font-paperlogy);
  font-size: 22px;
  font-weight: 700;
  color: var(--rayone-primary);
  opacity: 0.35;
  display: block;
  margin-bottom: 10px;
}

.m-design2-t {
  font-family: var(--font-pretendard);
  font-size: 15px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 8px;
}

.m-design2-d {
  font-family: var(--font-pretendard);
  font-size: 12px;
  color: #888;
  line-height: 1.6;
  margin: 0;
  word-break: keep-all;
}

/* ===== 세대안내 ===== */
.m-type {
  background: #fff;
  border-top: 1px solid #f0f0f0;
}

.m-type-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 20px;
}

.m-type-tab {
  flex: 1;
  min-width: 60px;
  min-height: 44px;
  padding: 10px 4px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-family: var(--font-paperlogy);
  font-size: 14px;
  color: #888;
  cursor: pointer;
  transition: all 0.25s ease;
}

.m-type-tab.is-active {
  background: var(--rayone-primary);
  border-color: var(--rayone-primary);
  color: #fff;
  font-weight: 700;
}

.m-type-img {
  width: 100%;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
  border-radius: 8px;
  padding: 12px;
}

.m-type-img img {
  max-width: 100%;
  height: auto;
  display: block;
}

/* ===== 커뮤니티 ===== */
.m-comm {
  background: #fff;
  border-top: 1px solid #f0f0f0;
}

.m-comm-list {
  border-top: 2px solid #1a1a1a;
  margin-top: 4px;
}

.m-comm-card {
  padding: 20px 0;
  border-bottom: 1px solid #eee;
}

.m-comm-no {
  font-family: var(--font-paperlogy);
  font-size: 22px;
  font-weight: 700;
  color: var(--rayone-primary);
  opacity: 0.35;
}

.m-comm-t {
  font-family: var(--font-pretendard);
  font-size: 16px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 8px 0 0;
  word-break: keep-all;
}

.m-comm-line {
  display: block;
  width: 24px;
  height: 1px;
  background: var(--rayone-primary);
  margin: 12px 0;
}

.m-comm-d {
  font-family: var(--font-pretendard);
  font-size: 13px;
  color: #777;
  line-height: 1.7;
  margin: 0;
  word-break: keep-all;
}

/* ===== 오시는 길 ===== */
.m-contact {
  background: #f5f5f3;
  border-top: 1px solid #ececec;
}

.m-map-card {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
  margin-bottom: 20px;
}

.m-map-img {
  position: relative;
}

.m-map-img img {
  width: 100%;
  height: auto;
  display: block;
}

.m-grand {
  position: absolute;
  bottom: 12px;
  right: 12px;
  width: 56px;
  height: 56px;
  background: #1a1a2e;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  line-height: 1.3;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.m-map-info {
  padding: 18px 18px 20px;
}

.m-map-label {
  display: block;
  font-size: 16px;
  font-weight: 700;
  color: #1a1a1a;
  letter-spacing: 2px;
  margin-bottom: 6px;
}

.m-map-addr {
  display: block;
  font-size: 13px;
  color: #666;
  margin-bottom: 14px;
  word-break: keep-all;
}

.m-map-btns {
  display: flex;
  gap: 8px;
}

.m-map-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 44px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #333;
  text-decoration: none;
}

.m-map-btn .n {
  color: #03c75a;
  font-weight: 800;
}

.m-map-btn .k {
  background: #fae100;
  color: #3a1d1d;
  font-weight: 800;
  font-size: 11px;
  width: 18px;
  height: 18px;
  border-radius: 3px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* ===== 방문예약 ===== */
.m-reserve {
  background: linear-gradient(140deg, var(--rayone-primary) 0%, var(--rayone-primary-dark) 100%);
  text-align: center;
}

.m-eyebrow.gold {
  color: #c9a96e;
}

.m-reserve-desc {
  font-family: var(--font-pretendard);
  font-size: 14px;
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.8;
  margin: 0 0 28px;
  word-break: keep-all;
}

.m-reserve-phone {
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
  text-decoration: none;
  padding: 16px 40px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  margin-bottom: 20px;
}

.m-reserve-phone-label {
  font-size: 12px;
  letter-spacing: 2px;
  color: rgba(255, 255, 255, 0.5);
}

.m-reserve-phone-num {
  font-family: var(--font-paperlogy);
  font-size: 30px;
  font-weight: 700;
  color: #fff;
  letter-spacing: 1px;
}

.m-reserve-btn {
  display: block;
  width: 100%;
  max-width: 320px;
  margin: 0 auto;
  min-height: 54px;
  background: #fff;
  border: none;
  border-radius: 8px;
  font-family: var(--font-pretendard);
  font-size: 15px;
  font-weight: 700;
  color: var(--rayone-primary);
  letter-spacing: 0.5px;
  cursor: pointer;
}

/* 이미지 전환 트랜지션 */
.m-fade-enter-active { transition: opacity 0.35s ease, transform 0.35s ease; }
.m-fade-leave-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.m-fade-enter-from { opacity: 0; transform: scale(0.97); }
.m-fade-leave-to { opacity: 0; transform: scale(1.02); }

/* ===== 푸터 ===== */
.m-footer {
  background: #0a0a0a;
  /* 하단 플로팅 버튼에 가리지 않도록 여백 */
  padding: 36px 24px 100px;
}

.m-footer-brand {
  font-family: var(--font-pretendard);
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 20px;
}

.m-footer-info {
  margin-bottom: 20px;
}

.m-footer-row {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 10px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.m-footer-row span {
  font-family: var(--font-pretendard);
  font-size: 11px;
  color: var(--rayone-primary-light);
  letter-spacing: 1px;
}

.m-footer-row b {
  font-family: var(--font-pretendard);
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  word-break: keep-all;
}

.m-footer-row b a {
  color: inherit;
  text-decoration: none;
}

.m-footer-notes {
  margin-bottom: 16px;
}

.m-footer-notes p {
  font-family: var(--font-pretendard);
  font-size: 10.5px;
  color: rgba(255, 255, 255, 0.3);
  line-height: 1.6;
  margin: 0 0 4px;
  word-break: keep-all;
}

.m-footer-copy {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-top: 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.m-footer-copy span {
  font-family: var(--font-pretendard);
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
}

.m-footer-copy a {
  font-family: var(--font-pretendard);
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
  text-decoration: underline;
  text-underline-offset: 2px;
  white-space: nowrap;
}
</style>
